import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, TrendingUp, ArrowRight, Loader2, TrendingDown, Percent } from "lucide-react";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";

export default function OwnerServices() {
  const [form, setForm] = useState({
    address: "", bedrooms: 4, bathrooms: 3, pool: false, hot_tub: false, lake_view: false,
    current_revenue: 60000, current_occupancy: 60,
  });
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [gross, setGross] = useState(60000);
  const [pmRate, setPmRate] = useState(20);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runEstimate = async () => {
    setLoading(true); setEstimate(null);
    try {
      const { data } = await api.post("/ai/revenue-estimate", form);
      setEstimate(data);
    } catch { toast.error("Estimate failed. Please try again."); }
    finally { setLoading(false); }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email) return toast.error("Name and email are required");
    try {
      await api.post("/owner-leads", { ...form, ...lead });
      setSent(true);
      toast.success("Thanks! Our team will reach out within one business day.");
    } catch { toast.error("Could not submit. Please try again."); }
  };

  const AIRBNB_RATE = 0.15;
  const airbnbFee = Math.round(gross * AIRBNB_RATE);
  const keepAfterAirbnb = gross - airbnbFee;
  const pmFee = Math.round(gross * (pmRate / 100));
  const netAirbnbPlusPm = gross - airbnbFee - pmFee;
  const netDirectPlusPm = gross - pmFee;

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
      <div className="max-w-2xl">
        <span className="overline">Owner Services</span>
        <h1 className="mt-2 text-4xl lg:text-5xl">Free revenue projection calculator</h1>
        <p className="mt-4 text-lg text-taupe">
          See what your property could earn with Elite STR's dynamic pricing and multi-channel marketing. Our AI analyzes your home in seconds.
        </p>
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        {/* CALCULATOR */}
        <div className="rounded-3xl card-lux p-7">
          <h2 className="mb-5 text-2xl">Your property</h2>
          <div className="space-y-5">
            <div>
              <label className="overline mb-2 block">Property address</label>
              <Input data-testid="calc-address" value={form.address} onChange={(e) => set("address", e.target.value)}
                placeholder="e.g. Fredericksburg, TX" className="rounded-2xl border-black/10" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline mb-2 block">Bedrooms · {form.bedrooms}</label>
                <Slider data-testid="calc-bedrooms" min={1} max={8} step={1} value={[form.bedrooms]} onValueChange={([v]) => set("bedrooms", v)} />
              </div>
              <div>
                <label className="overline mb-2 block">Bathrooms · {form.bathrooms}</label>
                <Slider data-testid="calc-bathrooms" min={1} max={8} step={1} value={[form.bathrooms]} onValueChange={([v]) => set("bathrooms", v)} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[["pool", "Pool"], ["hot_tub", "Hot Tub"], ["lake_view", "Lake View"]].map(([k, l]) => (
                <div key={k} className="flex items-center justify-between rounded-2xl border border-black/10 px-3 py-3">
                  <span className="text-sm">{l}</span>
                  <Switch data-testid={`calc-${k}`} checked={form[k]} onCheckedChange={(v) => set(k, v)} />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline mb-2 block">Current annual revenue</label>
                <Input data-testid="calc-revenue" type="number" value={form.current_revenue}
                  onChange={(e) => set("current_revenue", Number(e.target.value))} className="rounded-2xl border-black/10" />
              </div>
              <div>
                <label className="overline mb-2 block">Current occupancy %</label>
                <Input data-testid="calc-occupancy" type="number" value={form.current_occupancy}
                  onChange={(e) => set("current_occupancy", Number(e.target.value))} className="rounded-2xl border-black/10" />
              </div>
            </div>
            <Button data-testid="calc-estimate-btn" onClick={runEstimate} disabled={loading} className="h-12 w-full rounded-full btn-ink">
              {loading ? <><Loader2 size={18} className="mr-2 animate-spin" /> Analyzing…</> : <><Sparkles size={18} className="mr-2" /> Estimate my revenue</>}
            </Button>
          </div>
        </div>

        {/* RESULT + LEAD */}
        <div className="space-y-6">
          <div className="rounded-3xl bg-ink p-7 text-white">
            <div className="flex items-center gap-2 text-gold"><TrendingUp size={18} /> <span className="overline text-gold">AI Projection</span></div>
            {estimate ? (
              <div data-testid="estimate-result" className="mt-4 animate-fade-up">
                <div className="flex flex-wrap items-end gap-6">
                  <div><div className="text-5xl font-medium">{estimate.revenue}</div><div className="overline mt-1 text-white/50">Projected annual</div></div>
                  <div><div className="text-3xl font-medium text-camel">{estimate.occupancy}</div><div className="overline mt-1 text-white/50">Occupancy</div></div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-white/70">{estimate.rationale}</p>
              </div>
            ) : (
              <p className="mt-4 text-white/50">Enter your property details and let our AI project your earning potential with Elite STR management.</p>
            )}
          </div>

          <div className="rounded-3xl card-lux p-7">
            <h2 className="mb-1 text-2xl">Schedule a consultation</h2>
            <p className="mb-5 text-sm text-taupe">Free, no obligation. We'll build your custom plan.</p>
            {sent ? (
              <div data-testid="lead-success" className="rounded-2xl bg-cream p-6 text-center">
                <p className="text-lg text-ink">Request received ✦</p>
                <p className="mt-1 text-sm text-taupe">A specialist will contact you within one business day.</p>
              </div>
            ) : (
              <form onSubmit={submitLead} className="space-y-3">
                <Input data-testid="lead-name" placeholder="Full name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="rounded-2xl border-black/10" />
                <div className="grid grid-cols-2 gap-3">
                  <Input data-testid="lead-email" type="email" placeholder="Email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="rounded-2xl border-black/10" />
                  <Input data-testid="lead-phone" placeholder="Phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="rounded-2xl border-black/10" />
                </div>
                <Textarea data-testid="lead-message" placeholder="Tell us about your property (optional)" value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} className="rounded-2xl border-black/10" />
                <Button data-testid="lead-submit" type="submit" className="h-12 w-full rounded-full btn-ink">
                  Request Consultation <ArrowRight size={16} className="ml-1" />
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* TAKE-HOME CALCULATORS */}
      <div className="mt-16" data-testid="takehome-section">
        <div className="max-w-2xl">
          <span className="overline text-camel">Take-home calculator</span>
          <h2 className="mt-2 text-3xl lg:text-4xl">See what you actually keep</h2>
          <p className="mt-3 text-taupe">Enter your expected gross rental income to see how platform and management fees hit your bottom line — and how much booking direct puts back in your pocket.</p>
        </div>

        <div className="mb-6 mt-6 max-w-sm">
          <label className="overline mb-2 block">Expected gross annual revenue</label>
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-taupe">$</span>
            <Input data-testid="takehome-gross" type="number" min={0} value={gross}
              onChange={(e) => setGross(Math.max(0, Number(e.target.value)))} className="rounded-2xl border-black/10 pl-7" />
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Card 1: Airbnb fees */}
          <div className="rounded-3xl card-lux p-7" data-testid="calc-airbnb">
            <div className="flex items-center gap-2 text-camel"><TrendingDown size={18} /><span className="overline">Less Airbnb fees</span></div>
            <h3 className="mt-2 text-2xl">On Airbnb (15% fees)</h3>
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-taupe">Gross revenue</span><span className="font-medium text-ink">{money(gross)}</span></div>
              <div className="flex justify-between"><span className="text-taupe">Airbnb platform fees (15%)</span><span className="font-medium text-red-600" data-testid="airbnb-fee">− {money(airbnbFee)}</span></div>
              <div className="flex justify-between border-t border-black/5 pt-3 text-base"><span className="font-semibold text-ink">You keep on Airbnb</span><span className="font-semibold text-ink" data-testid="airbnb-net">{money(keepAfterAirbnb)}</span></div>
            </div>
            <div className="mt-5 rounded-2xl bg-cream p-4 text-sm text-ink">
              Book direct with Elite STR and skip that 15% — <span className="font-semibold">{money(airbnbFee)}/yr</span> back in your pocket.
            </div>
          </div>

          {/* Card 2: Property management fees */}
          <div className="rounded-3xl bg-ink p-7 text-white" data-testid="calc-pm">
            <div className="flex items-center gap-2 text-gold"><Percent size={18} /><span className="overline text-gold">Add management fee</span></div>
            <h3 className="mt-2 text-2xl">Net after property management</h3>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {[15, 20, 25].map((r) => (
                <button key={r} type="button" data-testid={`pm-rate-${r}`} onClick={() => setPmRate(r)}
                  className={`rounded-full px-4 py-1.5 text-sm transition-colors ${pmRate === r ? "bg-gold text-ink" : "bg-white/10 text-white hover:bg-white/20"}`}>{r}%</button>
              ))}
              <div className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1">
                <Input data-testid="pm-rate-custom" type="number" min={0} max={100} value={pmRate}
                  onChange={(e) => setPmRate(Math.min(100, Math.max(0, Number(e.target.value))))}
                  className="h-7 w-14 border-0 bg-transparent p-0 text-center text-white focus-visible:ring-0" />
                <span className="text-sm text-white/70">%</span>
              </div>
            </div>
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-white/60">Management fee ({pmRate}%)</span><span className="font-medium text-white" data-testid="pm-fee">− {money(pmFee)}</span></div>
              <div className="flex justify-between"><span className="text-white/60">Net: Airbnb + management</span><span className="font-medium text-white" data-testid="net-airbnb-pm">{money(netAirbnbPlusPm)}</span></div>
              <div className="flex justify-between border-t border-white/10 pt-3 text-base"><span className="font-semibold text-gold">Net: Direct + management</span><span className="font-semibold text-gold" data-testid="net-direct-pm">{money(netDirectPlusPm)}</span></div>
            </div>
            <p className="mt-4 text-sm text-white/70">That's <span className="font-semibold text-white">{money(airbnbFee)}/yr more</span> by booking direct — same management, no Airbnb cut.</p>
          </div>
        </div>
      </div>

      <div className="mt-10 text-center text-sm text-taupe">
        Want the full-service breakdown? <Link to="/property-management" className="text-camel underline">See how we manage your property →</Link>
      </div>
    </div>
  );
}
